import React, { useState, useEffect } from 'react';
import { Table, Badge, Card, Spinner, Row, Col, Button, ProgressBar, Collapse, Modal } from 'react-bootstrap';
import axios from 'axios';
import { format } from 'date-fns'; 

// --- ICONS ---
const LocationIcon = (props) => <i className="bi bi-geo-alt-fill" {...props}></i>;
const TimeIcon = (props) => <i className="bi bi-clock-history" {...props}></i>;
const CalendarIcon = (props) => <i className="bi bi-calendar-check" {...props}></i>;
const ScheduleIcon = (props) => <i className="bi bi-play-circle-fill" {...props}></i>;
const PhotoIcon = (props) => <i className="bi bi-images" {...props}></i>;
const CheckCircleIcon = (props) => <i className="bi bi-check-circle-fill" {...props}></i>;
const DownloadIcon = (props) => <i className="bi bi-file-earmark-pdf-fill" {...props}></i>;
const DescriptionIcon = (props) => <i className="bi bi-file-text" {...props}></i>;
const BuildIcon = (props) => <i className="bi bi-tools" {...props}></i>;
const CleaningIcon = (props) => <i className="bi bi-bucket-fill" {...props}></i>;
const EngineeringIcon = (props) => <i className="bi bi-gear-fill" {...props}></i>;
const BoltIcon = (props) => <i className="bi bi-lightning-fill" {...props}></i>;
const WarningIcon = (props) => <i className="bi bi-exclamation-triangle-fill" {...props}></i>;
const CloseIcon = (props) => <i className="bi bi-x-circle-fill" {...props}></i>;
const CheckBoxIcon = (props) => <i className="bi bi-check-square-fill" {...props}></i>;

// --- STYLES ---
const styles = {
    pageContainer: { backgroundColor: '#F4F7FE', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", paddingBottom: '40px' },
    purpleHeader: {
        background: "linear-gradient(135deg,#6528a9, #cc2899)",
        borderRadius: '20px', color: 'white', padding: '30px 40px', marginBottom: '30px',
        boxShadow: '0px 10px 20px rgba(118, 56, 250, 0.2)', position: 'relative', overflow: 'hidden'
    },
    headerControls: {
        backgroundColor: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(10px)', borderRadius: '12px',
        padding: '5px 10px', display: 'flex', alignItems: 'center', border: '1px solid rgba(255,255,255,0.3)'
    },
    kpiCard: {
        border: 'none', borderRadius: '20px', boxShadow: '0 5px 15px rgba(0,0,0,0.03)', padding: '20px',
        height: '100%', backgroundColor: '#fff', position: 'relative'
    },
    kpiLabel: { color: '#A3AED0', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' },
    kpiValue: { color: '#2B3674', fontSize: '32px', fontWeight: '700', marginTop: '5px' },
    iconCircle: {
        width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: '1.5rem', position: 'absolute', right: '20px', top: '20px'
    },
    tableCard: {
        border: 'none', borderRadius: '20px', boxShadow: '0 5px 15px rgba(0,0,0,0.03)',
        backgroundColor: '#fff', overflow: 'hidden', padding: '20px'
    },
    tableHeader: { color: '#A3AED0', fontSize: '12px', fontWeight: '700', borderBottom: '1px solid #E9EDF7', textTransform: 'uppercase' },
    tableRow: { borderBottom: '1px solid #f5f5f5', transition: '0.2s' },
    timelineContainer: { display: 'flex', flexWrap: 'wrap', gap: '20px', padding: '20px 40px', justifyContent: 'flex-start' },
    stepBox: {
        position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '110px',
        textAlign: 'center', padding: '10px', borderRadius: '12px', backgroundColor: '#fff',
        border: '1px solid #f0f0f0', transition: '0.2s all'
    },
    stepCircle: (status) => ({
        width: '36px', height: '36px', borderRadius: '50%',
        backgroundColor: status === 'done' ? '#05CD99' : (status === 'extra' ? '#6528a9' : '#fff'),
        border: status === 'done' || status === 'extra' ? 'none' : '2px dashed #A3AED0',
        color: status === 'done' || status === 'extra' ? 'white' : '#A3AED0',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold',
        fontSize: '14px', marginBottom: '8px',
        boxShadow: status === 'done' ? '0 4px 10px rgba(5, 205, 153, 0.4)' : 'none'
    }),
    stepLabel: { fontSize: '12px', fontWeight: '700', color: '#2B3674' },
    stepDate: { fontSize: '11px', color: '#707EAE', marginTop: '2px', fontWeight: '600' },
    stepTime: { fontSize: '10px', color: '#A3AED0' }
};

// --- HELPER FUNCTIONS ---
const safeParsePhotos = (photoData) => {
    if (!photoData) return [];
    if (Array.isArray(photoData)) return photoData;
    try {
        const parsed = JSON.parse(photoData);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return typeof photoData === 'string' && photoData.length > 5 ? [photoData] : [];
    }
};

const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircleIcon style={{fontSize: '14px'}} />;
    if (status === 'scheduled') return <CalendarIcon style={{fontSize: '14px'}} />;
    return <ScheduleIcon style={{fontSize: '14px'}} />;
};

const cleanPhotoUrl = (url) => {
    if (!url) return '';
    let cleanUrl = url.toString().trim().replace(/["\[\]]/g, '').replace(/%22/g, '').replace(/%5D/g, '');
    if (cleanUrl.startsWith('http')) return cleanUrl;
    if (cleanUrl.startsWith('/')) return `https://app.sowashusa.com${cleanUrl}`;
    return `https://app.sowashusa.com/uploads/job-photos/${cleanUrl}`;
};

const AccountsDashboard = () => {
    const [financeData, setFinanceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewDate, setViewDate] = useState(new Date());
    const [openSite, setOpenSite] = useState(null);

    // Modal States
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    // --- FETCH DATA FOR DASHBOARD ---
    const fetchData = async (targetDate) => {
        try {
            setLoading(true);
            const m = targetDate.getMonth() + 1;
            const y = targetDate.getFullYear();
            const baseURL = 'https://app.sowashusa.com';
            
            const [resSales, resComm] = await Promise.all([
                axios.get(`${baseURL}/api/sales/sales-orders`),
                axios.get(`${baseURL}/api/schedule/calendar/${m}/${y}`)
            ]);

            const sortLogs = (logs) => {
                return logs.sort((a, b) => {
                    const dateA = new Date(a.scheduled_date || a.date || a.updatedAt || 0);
                    const dateB = new Date(b.scheduled_date || b.date || b.updatedAt || 0);
                    return dateA - dateB;
                });
            };

            // 1. Residential
            const residential = (resSales.data.data || []).map(order => {
                const completedCount = order.so_completed ? 1 : 0; 
                const historyItem = { 
                    ...order, 
                    schedule_id: order.id, 
                    date: order.updatedAt || order.createdAt, 
                    status: 'completed',
                    site_name: order.client_name + " (Res)",
                    client_name: order.client_name,
                    job_type: 'residential'
                };
                const history = order.so_completed ? [historyItem] : [];
                const needed = Math.max(4, history.length); 
                return {
                    name: order.client_name,
                    category: 'Residential',
                    washes: completedCount,
                    totalNeeded: needed,
                    status: completedCount >= 4 ? 'Completed' : 'In Progress',
                    history: history
                };
            });

            // 2. Commercial
            const commercial = (resComm.data.clients || []).map(client => {
                const rawLogs = client.schedules || [];
                const completedLogs = rawLogs.filter(s => s.status === 'completed');
                
                // CRITICAL: Mapping _id to schedule_id
                const enrichedLogs = completedLogs.map(log => ({
                    ...log,
                    schedule_id: log._id || log.id || log.schedule_id, // Ensure we get the ID
                    client_name: client.client_name,
                    site_name: log.site_name || client.client_name,
                    job_type: 'commercial'
                }));

                const history = sortLogs(enrichedLogs);
                const completedCount = history.length;
                const needed = Math.max(4, completedCount);

                return {
                    name: client.client_name,
                    category: 'Commercial',
                    washes: completedCount,
                    totalNeeded: needed,
                    status: completedCount >= 4 ? 'Completed' : 'In Progress',
                    history: history
                };
            });

            setFinanceData([...residential, ...commercial]);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(viewDate); }, [viewDate]);

    // --- HANDLE CLICK & FETCH DETAIL ---
    const handleWashClick = async (logData) => {
        if (!logData) return;
        
        setDetailsOpen(true);
        setDetailsLoading(true);
        
        // Show summary data initially
        setSelectedJob(logData); 

        try {
            // Find ID (Crucial Step)
            const idToFetch = logData.schedule_id || logData._id || logData.id;

            if (idToFetch) {
                const baseURL = 'https://app.sowashusa.com';
                // Fetch FULL details using the ID
                const response = await axios.get(`${baseURL}/api/fo-schedules/${idToFetch}/detail`);
                
                if (response.data && response.data.job) {
                    console.log("Detail fetched:", response.data.job);
                    // Update state with full details
                    setSelectedJob(response.data.job);
                }
            } else {
                console.warn("No ID found for detail fetch");
            }
        } catch (error) {
            console.error("Error fetching full details:", error);
        } finally {
            setDetailsLoading(false);
        }

    };


    const handleDownloadReport = () => {
        alert("Downloading Report...");
    };

    // --- Helpers ---
    const totalClients = financeData.length;
    const completedSites = financeData.filter(item => item.status === 'Completed').length;
    const inProgressSites = financeData.filter(item => item.status === 'In Progress').length;

    const adjustMonth = (offset) => {
        const nextDate = new Date(viewDate.setMonth(viewDate.getMonth() + offset));
        setViewDate(new Date(nextDate));
    };

    const formatDate = (log) => {
        if(!log) return '-';
        const dateObj = new Date(log.scheduled_date || log.date || log.updatedAt || log.createdAt);
        if (isNaN(dateObj)) return "Pending";
        return dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatTime = (log) => {
        if(!log) return '';
        const dateObj = new Date(log.scheduled_date || log.date || log.updatedAt || log.createdAt);
        if (isNaN(dateObj)) return "";
        return dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div style={styles.pageContainer} className="px-3 px-md-5 py-4">
            
            {/* Header */}
            <div style={styles.purpleHeader}>
                <Row className="align-items-center">
                    <Col md={8}>
                        <h2 className="mb-1 fw-bold text-white">Accounts Dashboard</h2>
                        <p className="mb-0" style={{ opacity: 0.8 }}>Performance Overview & Monthly Tracking</p>
                    </Col>
                    <Col md={4} className="d-flex justify-content-md-end mt-3 mt-md-0">
                        <div style={styles.headerControls}>
                            <Button variant="link" className="text-white p-0 border-0 text-decoration-none" onClick={() => adjustMonth(-1)} style={{fontSize: '1.2rem'}}>‹</Button>
                            <div className="px-3 fw-bold text-white text-center" style={{minWidth: '140px'}}>
                                📅 {viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                            </div>
                            <Button variant="link" className="text-white p-0 border-0 text-decoration-none" onClick={() => adjustMonth(1)} style={{fontSize: '1.2rem'}}>›</Button>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* KPI Cards */}
            <Row className="g-4 mb-4">
                <Col md={4}><Card style={styles.kpiCard}><div><div style={styles.kpiLabel}>Total Clients</div><div style={styles.kpiValue}>{totalClients}</div></div><div style={{...styles.iconCircle, backgroundColor: '#FFF7E6', color: '#FFB547'}}>⚡</div></Card></Col>
                <Col md={4}><Card style={styles.kpiCard}><div><div style={styles.kpiLabel}>Completion Rate</div><div style={styles.kpiValue}>{totalClients > 0 ? Math.round((completedSites/totalClients)*100) : 0}%</div></div><div style={{...styles.iconCircle, backgroundColor: '#E6FBF5', color: '#05CD99'}}>🛡️</div></Card></Col>
                <Col md={4}><Card style={styles.kpiCard}><div><div style={styles.kpiLabel}>Pending Actions</div><div style={styles.kpiValue}>{inProgressSites}</div></div><div style={{...styles.iconCircle, backgroundColor: '#FFE2E5', color: '#F64E60'}}>❗</div></Card></Col>
            </Row>

            {/* Main Table */}
            <Card style={styles.tableCard}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="fw-bold text-dark mb-0">Client Portfolio & Status</h5>
                </div>
                
                <Table responsive hover className="align-middle mb-0">
                    <thead className="bg-white">
                        <tr>
                            <th style={styles.tableHeader} className="ps-3 py-3">CLIENT NAME</th>
                            <th style={styles.tableHeader} className="text-center">CATEGORY</th>
                            <th style={styles.tableHeader}>PROGRESS TRACKING</th>
                            <th style={styles.tableHeader} className="text-center">STATUS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="4" className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr>
                        ) : financeData.map((item, index) => (
                            <React.Fragment key={index}>
                                {/* Row */}
                                <tr onClick={() => setOpenSite(openSite === index ? null : index)} style={{...styles.tableRow, cursor: 'pointer', backgroundColor: openSite === index ? '#F8F9FA' : 'transparent', borderLeft: openSite === index ? '4px solid #6528a9' : '4px solid transparent'}}>
                                    <td className="ps-3 py-3">
                                        <div className="fw-bold text-dark" style={{fontSize: '0.95rem'}}>{item.name}</div>
                                        <div className="text-muted small" style={{fontSize: '0.75rem'}}>ID: #{1000 + index}</div>
                                    </td>
                                    <td className="text-center">
                                        <Badge bg="light" text="dark" className="border px-3 py-2 rounded-pill fw-normal">{item.category}</Badge>
                                    </td>
                                    <td style={{minWidth: '220px'}}>
                                        <div className="d-flex align-items-center">
                                            <div className="me-3 fw-bold text-dark">{Math.round((item.washes / item.totalNeeded) * 100)}%</div> 
                                            <div className="flex-grow-1">
                                                <ProgressBar 
                                                    now={Math.min((item.washes / item.totalNeeded) * 100, 100)} 
                                                    variant={item.washes > 4 ? "info" : (item.status === 'Completed' ? "success" : "warning")} 
                                                    style={{height: '6px', backgroundColor: '#E0E5F2'}} 
                                                    className="rounded-pill" 
                                                />
                                            </div>
                                            <div className="ms-3 text-muted small fw-bold">{item.washes}/{item.totalNeeded}</div>
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        {item.status === 'Completed' ? (
                                            <span style={{color: '#05CD99', backgroundColor: '#E6FBF5', padding: '5px 15px', borderRadius: '30px', fontWeight: '700', fontSize: '0.8rem'}}>Check</span>
                                        ) : (
                                            <span style={{color: '#FFB547', backgroundColor: '#FFF7E6', padding: '5px 15px', borderRadius: '30px', fontWeight: '700', fontSize: '0.8rem'}}>Pending</span>
                                        )}
                                    </td>
                                </tr>
                                
                                {/* Expanded Details */}
                                <tr>
                                    <td colSpan="4" className="p-0 border-0">
                                        <Collapse in={openSite === index}>
                                            <div style={{backgroundColor: '#F8F9FA', borderBottom: '1px solid #ddd', paddingBottom: '20px'}}>
                                                <div className="px-4 pt-3 d-flex justify-content-between">
                                                    <h6 className="fw-bold text-muted small mb-0">WASH HISTORY LOGS</h6>
                                                    <small className="text-muted">Target: {item.totalNeeded} Washes</small>
                                                </div>
                                                
                                                <div style={styles.timelineContainer}>
                                                    {[...Array(item.totalNeeded)].map((_, i) => {
                                                        const logData = item.history[i];
                                                        const isCompleted = !!logData;
                                                        const isExtra = i >= 4;

                                                        return (
                                                            <div 
                                                                key={i} 
                                                                onClick={() => isCompleted ? handleWashClick(logData) : null}
                                                                style={{
                                                                    ...styles.stepBox,
                                                                    borderColor: isCompleted ? '#05CD99' : '#E0E5F2',
                                                                    boxShadow: isCompleted ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
                                                                    cursor: isCompleted ? 'pointer' : 'default',
                                                                }}
                                                                onMouseEnter={(e) => { if(isCompleted) e.currentTarget.style.transform = 'translateY(-3px)' }}
                                                                onMouseLeave={(e) => { if(isCompleted) e.currentTarget.style.transform = 'translateY(0)' }}
                                                            >
                                                                <div style={styles.stepCircle(isCompleted ? 'done' : (isExtra ? 'extra' : 'pending'))}>
                                                                    {isCompleted ? '✓' : i + 1}
                                                                </div>
                                                                <div style={styles.stepLabel}>{isExtra ? `Extra ${i+1}` : `Wash ${i + 1}`}</div>
                                                                <div style={styles.stepDate}>{isCompleted ? formatDate(logData) : '-'}</div>
                                                                <div style={styles.stepTime}>{isCompleted ? formatTime(logData) : ''}</div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                            </div>
                                        </Collapse>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
            </Card>

{/* Job Details Modal */}
      <Modal
        show={detailsOpen}
        onHide={() => setDetailsOpen(false)}
        centered
        size="lg"
        style={{ zIndex: 9999 }}
      >
        <div
          style={{
            borderRadius: "20px",
            overflow: "hidden",
            border: "none",
          }}
        >
          <Modal.Header
            closeButton
            style={{
              background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
              border: "none",
              padding: "28px 32px",
            }}
          >
            {selectedJob && (
              <Modal.Title
                style={{
                  color: "white",
                  fontSize: "24px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <DescriptionIcon style={{ fontSize: "28px" }} />
                  {selectedJob.site_name}
                </div>
                <span
                  style={{
                    padding: "6px 14px",
                    background: "rgba(255,255,255,0.2)",
                    borderRadius: "10px",
                    fontSize: "12px",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    border: "2px solid rgba(255,255,255,0.3)",
                  }}
                >
                  {getStatusIcon(selectedJob.status)}{" "}
                  {selectedJob.status.replace("_", " ")}
                </span>
              </Modal.Title>
            )}
          </Modal.Header>

          {detailsLoading ? (
            <div style={{ padding: "80px", textAlign: "center" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  border: "4px solid #E9ECEF",
                  borderTop: "4px solid #667EEA",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 16px",
                }}
              />
              <p style={{ color: "#6C757D", fontWeight: "500" }}>
                Loading details...
              </p>
            </div>
          ) : (
            selectedJob && (
              <>
                <Modal.Body
                  style={{
                    padding: "32px",
                    maxHeight: "70vh",
                    overflowY: "auto",
                  }}
                >

                 {/* SITE INFORMATION */}
                                                  <div style={{ background: "#F9FAFB", padding: "24px", borderRadius: "16px", marginBottom: "24px", border: "2px solid #E5E7EB" }}>
                                                      <h6 style={{ fontSize: "16px", fontWeight: "700", color: "#111827", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}><LocationIcon style={{ color: "#667EEA", fontSize: "22px" }} /> Site Information</h6>
                                                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                                                          <div><div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "600", marginBottom: "4px", textTransform: "uppercase" }}>Client</div><div style={{ fontSize: "14px", color: "#111827", fontWeight: "600" }}>{selectedJob.client_name}</div></div>
                                                          <div><div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "600", marginBottom: "4px", textTransform: "uppercase" }}>Job Type</div><div style={{ fontSize: "14px", color: "#111827", fontWeight: "600" }}>{selectedJob.job_type === "maintenance" ? "Maintenance Service" : "Commercial Cleaning"}</div></div>
                                                          {selectedJob.system_size > 0 && (<div><div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "600", marginBottom: "4px", textTransform: "uppercase" }}>System Size</div><div style={{ fontSize: "14px", color: "#111827", fontWeight: "600" }}>{selectedJob.system_size} kW</div></div>)}
                                                          <div><div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "600", marginBottom: "4px", textTransform: "uppercase" }}>Service Number</div><div style={{ fontSize: "14px", color: "#111827", fontWeight: "600" }}>#{selectedJob.service_number}</div></div>
                                                          {selectedJob.contact_person && (<div><div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "600", marginBottom: "4px", textTransform: "uppercase" }}>Contact Person</div><div style={{ fontSize: "14px", color: "#111827", fontWeight: "600" }}>{selectedJob.contact_person}</div></div>)}
                                                          {selectedJob.contact_number && (<div><div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "600", marginBottom: "4px", textTransform: "uppercase" }}>Contact Number</div><div style={{ fontSize: "14px", color: "#111827", fontWeight: "600" }}>{selectedJob.contact_number}</div></div>)}
                                                          {selectedJob.team_lead_name && (<div><div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "600", marginBottom: "4px", textTransform: "uppercase" }}>Team Lead</div><div style={{ fontSize: "14px", color: "#111827", fontWeight: "600" }}>{selectedJob.team_lead_name}</div></div>)}
                                                          {selectedJob.address && (<div style={{ gridColumn: "1 / -1" }}><div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "600", marginBottom: "4px", textTransform: "uppercase" }}>Address</div><div style={{ fontSize: "14px", color: "#111827", fontWeight: "600" }}>{selectedJob.address}, {selectedJob.city} {selectedJob.state ? ", " + selectedJob.state : ""}</div></div>)}
                                                      </div>
                                                  </div>

                  {/* Job Timeline */}
                  <div
                    style={{
                      background: "#F9FAFB",
                      padding: "24px",
                      borderRadius: "16px",
                      marginBottom: "24px",
                      border: "2px solid #E5E7EB",
                    }}
                  >
                    <h6
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#111827",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <TimeIcon
                        style={{ color: "#667EEA", fontSize: "22px" }}
                      />
                      Job Timeline
                    </h6>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      {/* Scheduled */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "start",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "12px",
                            background: "#EFF6FF",
                            border: "2px solid #3B82F6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <CalendarIcon
                            style={{ color: "#3B82F6", fontSize: "20px" }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#3B82F6",
                              marginBottom: "2px",
                            }}
                          >
                            Scheduled
                          </div>
                          <div style={{ fontSize: "12px", color: "#6B7280" }}>
                            {format(
                              new Date(selectedJob.scheduled_date),
                              "MMM dd, yyyy",
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Reached Location */}
                      {selectedJob.reached_location_at && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "start",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "12px",
                              background: "#FEF3C7",
                              border: "2px solid #F59E0B",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <LocationIcon
                              style={{ color: "#F59E0B", fontSize: "20px" }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#F59E0B",
                                marginBottom: "2px",
                              }}
                            >
                              Reached Location
                            </div>
                            <div style={{ fontSize: "12px", color: "#6B7280" }}>
                              {format(
                                new Date(selectedJob.reached_location_at),
                                "MMM dd, yyyy • hh:mm a",
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Started */}
                      {selectedJob.started_at && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "start",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "12px",
                              background: "#ECFDF5",
                              border: "2px solid #10B981",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <ScheduleIcon
                              style={{ color: "#10B981", fontSize: "20px" }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#10B981",
                                marginBottom: "2px",
                              }}
                            >
                              Job Started
                            </div>
                            <div style={{ fontSize: "12px", color: "#6B7280" }}>
                              {format(
                                new Date(selectedJob.started_at),
                                "MMM dd, yyyy • hh:mm a",
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Before Photos */}
                      {selectedJob.before_photos_at && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "start",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "12px",
                              background: "#F5F3FF",
                              border: "2px solid #8B5CF6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <PhotoIcon
                              style={{ color: "#8B5CF6", fontSize: "20px" }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#8B5CF6",
                                marginBottom: "2px",
                              }}
                            >
                              Before Photos Uploaded
                            </div>
                            <div style={{ fontSize: "12px", color: "#6B7280" }}>
                              {format(
                                new Date(selectedJob.before_photos_at),
                                "MMM dd, yyyy • hh:mm a",
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* After Photos */}
                      {selectedJob.after_photos_at && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "start",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "12px",
                              background: "#FDF2F8",
                              border: "2px solid #EC4899",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <PhotoIcon
                              style={{ color: "#EC4899", fontSize: "20px" }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#EC4899",
                                marginBottom: "2px",
                              }}
                            >
                              After Photos Uploaded
                            </div>
                            <div style={{ fontSize: "12px", color: "#6B7280" }}>
                              {format(
                                new Date(selectedJob.after_photos_at),
                                "MMM dd, yyyy • hh:mm a",
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Completed */}
                      {selectedJob.completed_at && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "start",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "12px",
                              background: "#ECFDF5",
                              border: "2px solid #10B981",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <CheckCircleIcon
                              style={{ color: "#10B981", fontSize: "20px" }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#10B981",
                                marginBottom: "2px",
                              }}
                            >
                              Job Completed
                            </div>
                            <div style={{ fontSize: "12px", color: "#6B7280" }}>
                              {format(
                                new Date(selectedJob.completed_at),
                                "MMM dd, yyyy • hh:mm a",
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Photos Gallery */}
                  {(selectedJob.before_photos || selectedJob.after_photos) && (
                    <div
                      style={{
                        background: "#F9FAFB",
                        padding: "24px",
                        borderRadius: "16px",
                        marginBottom: "24px",
                        border: "2px solid #E5E7EB",
                      }}
                    >
                      <h6
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#111827",
                          marginBottom: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <PhotoIcon
                          style={{ color: "#667EEA", fontSize: "22px" }}
                        />
                        Photo Gallery
                      </h6>

                      {/* Before Photos */}
                      {selectedJob.before_photos &&
                        JSON.parse(selectedJob.before_photos).length > 0 && (
                          <div style={{ marginBottom: "20px" }}>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#6B7280",
                                marginBottom: "12px",
                              }}
                            >
                              Before Photos
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fill, minmax(150px, 1fr))",
                                gap: "12px",
                              }}
                            >
                              {JSON.parse(selectedJob.before_photos).map(
                                (photo, index) => (
                                  <img
                                    key={index}
                                    src={`https://app.sowashusa.com/uploads/job-photos/${photo.split("/").pop()}`}
                                    alt={`Before ${index + 1}`}
                                    style={{
                                      width: "100%",
                                      height: "150px",
                                      objectFit: "cover",
                                      borderRadius: "12px",
                                      cursor: "pointer",
                                      border: "2px solid #E5E7EB",
                                      transition: "transform 0.2s",
                                    }}
                                    onClick={() =>
                                      window.open(
                                        `https://app.sowashusa.com/uploads/job-photos/${photo.split("/").pop()}`,
                                        "_blank",
                                      )
                                    }
                                    onMouseOver={(e) =>
                                      (e.target.style.transform = "scale(1.05)")
                                    }
                                    onMouseOut={(e) =>
                                      (e.target.style.transform = "scale(1)")
                                    }
                                  />
                                ),
                              )}
                            </div>
                          </div>
                        )}

                      {/* After Photos */}
                      {selectedJob.after_photos &&
                        JSON.parse(selectedJob.after_photos).length > 0 && (
                          <div>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#6B7280",
                                marginBottom: "12px",
                              }}
                            >
                              After Photos
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fill, minmax(150px, 1fr))",
                                gap: "12px",
                              }}
                            >
                              {JSON.parse(selectedJob.after_photos).map(
                                (photo, index) => (
                                  <img
                                    key={index}
                                    src={`https://app.sowashusa.com/uploads/job-photos/${photo.split("/").pop()}`}
                                    alt={`After ${index + 1}`}
                                    style={{
                                      width: "100%",
                                      height: "150px",
                                      objectFit: "cover",
                                      borderRadius: "12px",
                                      cursor: "pointer",
                                      border: "2px solid #E5E7EB",
                                      transition: "transform 0.2s",
                                    }}
                                    onClick={() =>
                                      window.open(
                                        `https://app.sowashusa.com/uploads/job-photos/${photo.split("/").pop()}`,
                                        "_blank",
                                      )
                                    }
                                    onMouseOver={(e) =>
                                      (e.target.style.transform = "scale(1.05)")
                                    }
                                    onMouseOut={(e) =>
                                      (e.target.style.transform = "scale(1)")
                                    }
                                  />
                                ),
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  {/* FSR Details */}
                  {selectedJob.status === "completed" && (
                    <div
                      style={{
                        background: "#F9FAFB",
                        padding: "24px",
                        borderRadius: "16px",
                        marginBottom: "24px",
                        border: "2px solid #E5E7EB",
                      }}
                    >
                      <h6
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#111827",
                          marginBottom: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <DescriptionIcon
                          style={{ color: "#667EEA", fontSize: "22px" }}
                        />
                        Field Service Report
                      </h6>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "12px",
                          marginBottom: "16px",
                        }}
                      >
                        <div
                          style={{
                            background: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            border: "1px solid #E5E7EB",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                            }}
                          >
                            Cable Condition
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "700",
                            }}
                          >
                            {selectedJob.cable_condition || "N/A"}
                          </div>
                        </div>
                        <div
                          style={{
                            background: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            border: "1px solid #E5E7EB",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                            }}
                          >
                            Panel Damage
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "700",
                            }}
                          >
                            {selectedJob.panel_damage || "N/A"}
                          </div>
                        </div>
                        <div
                          style={{
                            background: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            border: "1px solid #E5E7EB",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                            }}
                          >
                            Inverter Alarm
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "700",
                            }}
                          >
                            {selectedJob.inverter_alarm || "N/A"}
                          </div>
                        </div>
                        <div
                          style={{
                            background: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            border: "1px solid #E5E7EB",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                            }}
                          >
                            Potential Shading
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "700",
                            }}
                          >
                            {selectedJob.potential_shading || "N/A"}
                          </div>
                        </div>
                      </div>

                      {/* Observations */}
                      <div
                        style={{
                          background: "white",
                          padding: "16px",
                          borderRadius: "12px",
                          border: "1px solid #E5E7EB",
                          marginBottom: "16px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6B7280",
                            fontWeight: "600",
                            marginBottom: "8px",
                          }}
                        >
                          Observations
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          {selectedJob.rusting && (
                            <span
                              style={{
                                padding: "6px 12px",
                                background: "#FEE2E2",
                                color: "#991B1B",
                                borderRadius: "8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                border: "1px solid #FCA5A5",
                              }}
                            >
                              Rusting Observed
                            </span>
                          )}
                          {selectedJob.bird_dropping && (
                            <span
                              style={{
                                padding: "6px 12px",
                                background: "#FEF3C7",
                                color: "#92400E",
                                borderRadius: "8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                border: "1px solid #FCD34D",
                              }}
                            >
                              Bird Droppings
                            </span>
                          )}
                          {selectedJob.mos_and_debris && (
                            <span
                              style={{
                                padding: "6px 12px",
                                background: "#FEF3C7",
                                color: "#92400E",
                                borderRadius: "8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                border: "1px solid #FCD34D",
                              }}
                            >
                              Moss & Debris
                            </span>
                          )}
                          {selectedJob.earthing && (
                            <span
                              style={{
                                padding: "6px 12px",
                                background: "#FEE2E2",
                                color: "#991B1B",
                                borderRadius: "8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                border: "1px solid #FCA5A5",
                              }}
                            >
                              Earthing Fault
                            </span>
                          )}
                          {selectedJob.cash_collected && (
                            <span
                              style={{
                                padding: "6px 12px",
                                background: "#ECFDF5",
                                color: "#065F46",
                                borderRadius: "8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                border: "1px solid #A7F3D0",
                              }}
                            >
                              Cash Collected
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Customer Signature */}
                      {selectedJob.customer_signature && (
                        <div
                          style={{
                            background: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            border: "1px solid #E5E7EB",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Customer Signature
                          </div>
                          <img
                            src={
                              selectedJob.customer_signature.startsWith("http")
                                ? selectedJob.customer_signature
                                : `https://app.sowashusa.com/uploads/signatures/${selectedJob.customer_signature}`
                            }
                            alt="Customer Signature"
                            style={{
                              width: "100%",
                              maxWidth: "300px",
                              height: "100px",
                              objectFit: "contain",
                              border: "1px solid #E5E7EB",
                              borderRadius: "8px",
                              background: "white",
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  {selectedJob.notes && (
                    <div
                      style={{
                        background: "#FFFBEB",
                        padding: "20px",
                        borderRadius: "16px",
                        border: "2px solid #FCD34D",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "700",
                          color: "#92400E",
                          marginBottom: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <i className="bi bi-sticky-fill"></i>
                        Notes
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#78350F",
                          lineHeight: "1.6",
                        }}
                      >
                        {selectedJob.notes}
                      </div>
                    </div>
                  )}
                </Modal.Body>

                <Modal.Footer
                  style={{
                    borderTop: "2px solid #F3F4F6",
                    padding: "20px 32px",
                    background: "#FAFBFC",
                  }}
                >
                  <div style={{ display: "flex", gap: "12px", width: "100%" }}>
                    <button
                      onClick={() => setDetailsOpen(false)}
                      style={{
                        flex: 1,
                        padding: "14px",
                        background: "white",
                        color: "#374151",
                        border: "2px solid #E5E7EB",
                        borderRadius: "12px",
                        fontSize: "15px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = "#F3F4F6";
                        e.target.style.borderColor = "#D1D5DB";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = "white";
                        e.target.style.borderColor = "#E5E7EB";
                      }}
                    >
                      Close
                    </button>
                  </div>
                </Modal.Footer>
              </>
            )
          )}
        </div>
      </Modal>
        </div>
    );
};

export default AccountsDashboard;